import React, { useEffect, useState, useCallback, useImperativeHandle, forwardRef, useRef } from 'react';
import {
  Box,
  VStack,
  Text,
  IconButton,
  Flex,
  Divider,
  Spinner,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  Button,
  Tooltip,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { FiPlus, FiSearch, FiRefreshCw, FiMenu, FiMessageSquare, FiGrid, FiEdit, FiMoreHorizontal, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';

interface Conversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}

interface ConversationSidebarProps {
  onSelectConversation: (conversationId: string) => void;
  onNewChat: () => void;
  currentConversationId?: string;
  onToggleSidebar: () => void;
  onRenameConversation: (conversationId: string, newTitle: string) => Promise<boolean>;
  onDeleteConversation: (conversationId: string) => Promise<boolean>;
}

// Create a type for the ref to expose methods
export interface ConversationSidebarRefType {
  fetchConversations: (newOffset?: number, refresh?: boolean) => Promise<void>;
}

const ConversationSidebar = forwardRef<ConversationSidebarRefType, ConversationSidebarProps>(({
  onSelectConversation,
  onNewChat,
  currentConversationId,
  onToggleSidebar,
  onRenameConversation,
  onDeleteConversation,
}, ref) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken } = useAuth();
  
  // Add pagination state
  const [page, setPage] = useState(1);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const conversationsPerPage = 10;
  
  // Styling
  const bgColor = useColorModeValue('white', 'gray.800');
  const hoverBgColor = useColorModeValue('gray.100', 'gray.700');
  const selectedBgColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.200');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const buttonBgColor = useColorModeValue('white', 'gray.800');
  const buttonHoverBgColor = useColorModeValue('gray.50', 'gray.700');
  const buttonActiveColor = useColorModeValue('gray.100', 'gray.600');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconColor = useColorModeValue('gray.600', 'gray.400');
  
  // Add state for forcing updates to timestamps
  const [timeUpdateTrigger, setTimeUpdateTrigger] = useState(0);
  
  // Add state for rename functionality
  const { isOpen: isRenameOpen, onOpen: onRenameOpen, onClose: onRenameClose } = useDisclosure();
  const [renameTitle, setRenameTitle] = useState('');
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  
  // Add state for delete confirmation
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  
  // Add state for tracking if operations are in progress
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Effect to trigger timestamp updates every minute
  useEffect(() => {
    // Update timestamps every minute
    const intervalId = setInterval(() => {
      setTimeUpdateTrigger(prev => prev + 1);
    }, 60000); // 60 seconds
    
    // Cleanup on unmount
    return () => clearInterval(intervalId);
  }, []);
  
  // Filtered conversations based on search term
  const filteredConversations = conversations.filter(conv => 
    conv.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const fetchConversations = async (newOffset = 0, refresh = false) => {
    if (newOffset === 0 || refresh) {
      setLoading(true);
    }
    setError(null);
    
    try {
      const token = await getAccessToken();
      const baseUrl = process.env.REACT_APP_API_URL || 'http://api:8889';
      
      const response = await fetch(
        `${baseUrl}/chat/conversations?limit=${conversationsPerPage}&offset=${newOffset}`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        console.error('Failed to fetch conversations, status:', response.status);
        throw new Error(`Failed to fetch conversations: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Determine if there are more conversations to load
      setHasMore(data.length === conversationsPerPage);
      
      // If this is offset 0 or a refresh, replace the conversations
      // Otherwise append to existing conversations
      if (newOffset === 0 || refresh) {
        setConversations(data);
        setPage(1);
      } else {
        setConversations(prev => [...prev, ...data]);
        setPage(prev => prev + 1);
      }
      
      // Update the current offset for next load
      setOffset(newOffset + data.length);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  // Load more conversations
  const handleLoadMore = () => {
    fetchConversations(offset);
  };

  // Initial load and when getAccessToken changes
  useEffect(() => {
    fetchConversations(0, true);
  }, [getAccessToken]);

  // Updated formatDate function that will re-render when timeUpdateTrigger changes
  const formatDate = useCallback((dateString: string) => {
    try {
      // Fix for PostgreSQL timestamp format - convert to ISO format if needed
      const isoString = dateString.replace(' ', 'T') + 'Z';
      
      // Parse the date
      const date = new Date(isoString);
      
      // Calculate time difference in minutes
      const diffMinutes = Math.floor((Date.now() - date.getTime()) / (60 * 1000));
      
      // For very recent messages (less than 1 minute ago), show "just now"
      if (diffMinutes < 1) {
        return 'just now';
      }
      
      // For conversations within last 24 hours, use relative time format
      if (diffMinutes < 24 * 60) {
        return formatDistanceToNow(date, { addSuffix: true });
      }
      
      // For older conversations, show the date
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short', 
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date error';
    }
  }, [timeUpdateTrigger]); // Re-run when timeUpdateTrigger changes

  // Use useImperativeHandle to expose fetchConversations method
  useImperativeHandle(ref, () => ({
    fetchConversations: fetchConversations,
  }));

  // Handle rename
  const handleRenameClick = (e: React.MouseEvent, conversationId: string, title: string) => {
    e.stopPropagation(); // Prevent triggering the conversation selection
    setActiveConversationId(conversationId);
    setRenameTitle(title);
    onRenameOpen();
  };
  
  // Handle rename submit
  const handleRenameSubmit = async () => {
    if (!activeConversationId) return;
    
    setIsProcessing(true);
    const success = await onRenameConversation(activeConversationId, renameTitle);
    
    if (success) {
      // Update the local state
      setConversations(prev => 
        prev.map(conv => 
          conv.id === activeConversationId 
            ? { ...conv, title: renameTitle } 
            : conv
        )
      );
      onRenameClose();
    }
    
    setIsProcessing(false);
  };
  
  // Handle delete
  const handleDeleteClick = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation(); // Prevent triggering the conversation selection
    setActiveConversationId(conversationId);
    onDeleteOpen();
  };
  
  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!activeConversationId) return;
    
    setIsProcessing(true);
    const success = await onDeleteConversation(activeConversationId);
    
    if (success) {
      // Remove from local state
      setConversations(prev => prev.filter(conv => conv.id !== activeConversationId));
      onDeleteClose();
    }
    
    setIsProcessing(false);
  };

  return (
    <Box 
      h="100%" 
      bg={bgColor} 
      display="flex" 
      flexDirection="column"
      borderRight="1px" 
      borderColor="gray.200"
      boxShadow="sm"
    >
      {/* Top bar with sidebar toggle and new chat buttons */}
      <Flex justifyContent="space-between" alignItems="center" p={2}>
        <Tooltip label="Toggle sidebar" placement="right" hasArrow>
          <IconButton
            icon={<FiMenu />}
            onClick={onToggleSidebar}
            variant="ghost"
            aria-label="Toggle sidebar"
            size="md"
          />
        </Tooltip>
        
        <Tooltip label="New chat" placement="right" hasArrow>
          <IconButton
            icon={<FiEdit />}
            onClick={onNewChat}
            variant="ghost"
            aria-label="New chat"
            size="md"
          />
        </Tooltip>
      </Flex>
      
      {/* Search Box */}
      <Box px={3} py={2}>
        <InputGroup size="md">
          <InputLeftElement pointerEvents="none" color={iconColor}>
            <FiSearch />
          </InputLeftElement>
          <Input
            placeholder="Search conversations"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            borderRadius="md"
            _placeholder={{ color: mutedColor }}
            fontSize="md"
          />
        </InputGroup>
      </Box>
      
      {/* Time Period Labels */}
      <Text px={3} py={2} fontSize="sm" fontWeight="medium" color={mutedColor}>
        Today
      </Text>

      <Box flex="1" overflowY="auto" px={1}>
        {loading && page === 1 ? (
          <Flex justify="center" align="center" h="100px">
            <Spinner size="md" color={mutedColor} />
          </Flex>
        ) : error ? (
          <Text color="red.500" p={3} textAlign="center">
            {error}
          </Text>
        ) : filteredConversations.length === 0 ? (
          <Text color={mutedColor} p={3} textAlign="center">
            {searchTerm ? "No matching conversations" : "No conversations yet"}
          </Text>
        ) : (
          <VStack align="stretch" spacing={1}>
            {filteredConversations.map(conv => (
              <Box
                key={conv.id}
                px={3}
                py={2}
                borderRadius="md"
                cursor="pointer"
                bg={currentConversationId === conv.id ? selectedBgColor : 'transparent'}
                _hover={{ 
                  bg: hoverBgColor,
                  '.conversation-actions': { opacity: 1 }
                }}
                onClick={() => onSelectConversation(conv.id)}
                position="relative"
              >
                <Flex justify="space-between" align="center">
                  <Box overflow="hidden" flex="1">
                    <Text 
                      fontSize="sm" 
                      fontWeight={currentConversationId === conv.id ? "medium" : "normal"}
                      color={textColor}
                      noOfLines={1}
                    >
                      {conv.title}
                    </Text>
                    <Text fontSize="xs" color={mutedColor}>
                      {formatDistanceToNow(new Date(conv.updated_at.replace(' ', 'T') + 'Z'), { addSuffix: true })}
                    </Text>
                  </Box>
                  
                  <Box 
                    className="conversation-actions" 
                    opacity={{ base: "1", md: "0" }} 
                    transition="opacity 0.2s"
                    _groupHover={{ opacity: 1 }}
                  >
                    <Menu placement="bottom-end" closeOnSelect={true} gutter={0}>
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreHorizontal />}
                        variant="ghost"
                        aria-label="Options"
                        size="xs"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <MenuList zIndex={100} minWidth="120px">
                        <MenuItem 
                          icon={<FiEdit />} 
                          onClick={(e) => handleRenameClick(e, conv.id, conv.title)}
                        >
                          Rename
                        </MenuItem>
                        <MenuItem 
                          icon={<FiTrash2 />} 
                          color="red.500" 
                          onClick={(e) => handleDeleteClick(e, conv.id)}
                        >
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Box>
                </Flex>
              </Box>
            ))}
          </VStack>
        )}
        
        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            width="full"
            mt={2}
            onClick={handleLoadMore}
            isLoading={loading && page > 1}
          >
            Load more
          </Button>
        )}
      </Box>
      
      {/* Rename Modal */}
      <Modal isOpen={isRenameOpen} onClose={onRenameClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Rename Conversation</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              value={renameTitle}
              onChange={(e) => setRenameTitle(e.target.value)}
              placeholder="Enter new title"
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onRenameClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleRenameSubmit}
              isLoading={isProcessing}
              isDisabled={!renameTitle.trim()}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Conversation</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete this conversation? This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              onClick={handleDeleteConfirm}
              isLoading={isProcessing}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
});

export default ConversationSidebar; 